import type {
  ChatCorrection,
  ChatNeed,
  ChatResponse,
  ChatUnsupported,
  CompactCard,
  CuratedState,
} from '@citiguide/shared';
import type { RouteType } from '../../store/markerStore';

export const MAX_RESULT_CARDS = 8;

/** The store actions a response can trigger. Passed in so this stays a pure, unit-testable function. */
export interface ChatActions {
  applyState: (state: CuratedState) => void;
  appendAssistant: (text: string) => void;
  selectMarker: (markerId: string) => void;
  showRoute: (type: RouteType) => void;
  /** True once the browser has granted location (a location exists in the user store). */
  hasLocation: boolean;
  /** Pins currently held, saved with a `save_plan` action. */
  pinnedMarkerIds: string[];
  savePlan: (plan: { name: string; state: CuratedState; pinnedMarkerIds: string[] }) => { name: string } | null;
  /** Localized "My plan <date>" used when the model gave no plan_name. */
  defaultPlanName: string;
}

/** What the transcript renders for one response, beyond the reply text itself. */
export interface AppliedResponse {
  needs: ChatNeed[];
  unsupported: ChatUnsupported[];
  corrections: ChatCorrection[];
  items: CompactCard[];
  total: number;
  /** chips_only mode or no reply: the assistant is resting, the chips still work. */
  resting: boolean;
  /** navigate not_found: candidates the visitor can tap (no model call). */
  didYouMean: Array<{ id: string; title: string }>;
  /** navigate ok but no location yet: ask the visitor to share it for directions. */
  directionsNeedLocation: boolean;
  savedPlanName: string | null;
}

/**
 * Apply one ChatResponse. The state always REPLACES the whole filter (it is the full desired state,
 * never a delta). Components never parse a ChatResponse themselves.
 */
export function applyChatResponse(response: ChatResponse, actions: ChatActions): AppliedResponse {
  actions.applyState(response.state);
  if (response.reply) actions.appendAssistant(response.reply);

  let didYouMean: AppliedResponse['didYouMean'] = [];
  let directionsNeedLocation = false;
  let savedPlanName: string | null = null;

  if (response.action === 'navigate' && response.navigate) {
    if (response.navigate.status === 'ok' && response.navigate.target_id) {
      actions.selectMarker(response.navigate.target_id);
      actions.showRoute('walking');
      directionsNeedLocation = !actions.hasLocation;
    } else {
      didYouMean = response.navigate.candidates ?? [];
    }
  }

  if (response.action === 'save_plan') {
    const saved = actions.savePlan({
      name: response.plan_name?.trim() || actions.defaultPlanName,
      state: response.state,
      pinnedMarkerIds: actions.pinnedMarkerIds,
    });
    savedPlanName = saved?.name ?? null;
  }

  return {
    needs: response.needs ?? [],
    unsupported: response.unsupported ?? [],
    corrections: response.corrections ?? [],
    items: (response.results?.items ?? []).slice(0, MAX_RESULT_CARDS),
    total: response.results?.total ?? 0,
    resting: response.meta?.mode === 'chips_only' || !response.reply,
    didYouMean,
    directionsNeedLocation,
    savedPlanName,
  };
}
